import type { ProductInformationEntry } from "../pages/productInformation/productInformationData";
import type { AppLocale } from "./locales";

export type ProductLocalizedCopy = {
  badgeText: string;
  h1Line1: string;
  h1Accent: string;
  subtitleLine1: string;
  subtitleLine2: string;
  sectionTitle: string;
  sectionBody: string;
  specs: { label: string; value: string }[];
  asideTitle: string;
  asideBody: string;
  ctaText: string;
  filterHint: string;
};

export const PRODUCT_COPY_OVERRIDES: Record<
  string,
  Partial<Record<"ja" | "zh-TW", ProductLocalizedCopy>>
> = {
  "pokemon-30th-celebration": {
    ja: {
      badgeText: "⏳ 2026年9月16日 発売",
      h1Line1: "メガセレブレーション",
      h1Accent: "「30th CELEBRATION」",
      subtitleLine1: "ポケモンカードと、あのころも、これからも。",
      subtitleLine2:
        "ポケモンカードゲーム30周年セレブレーション——ピカチュウとパーティーしよう！",
      sectionTitle: "奇跡の30年——セレブレーション、はじまる",
      sectionBody:
        "ポケモンカードゲーム30周年セレブレーションが登場！ピカチュウ、ミュウ、ミュウツーなど、おなじみの顔ぶれが再び集う。友情のきずなをもう一度結び、かつてないお祝いを楽しもう！",
      specs: [
        { label: "発売日", value: "2026年9月16日（水）" },
        { label: "シリーズ", value: "ポケモンカードゲーム MEGA" },
        { label: "言語", value: "日本語" },
        {
          label: "内容",
          value:
            "30周年記念カード、ピカチュウ、および幻のポケモンの冒険カードを収録",
        },
      ],
      asideTitle: "📍 セレブレーションに参加する準備はできた？",
      asideBody:
        "最新のブースターパックが欲しい？近くの公式認定カードショップを見つけて、今すぐ予約しよう！",
      ctaText: "認定店舗を探す",
      filterHint: "ポケモン商品を取り扱う店舗で絞り込みます",
    },
    "zh-TW": {
      badgeText: "⏳ 2026年9月16日 發售",
      h1Line1: "Mega Celebration",
      h1Accent: "「30th CELEBRATION」",
      subtitleLine1: "與寶可夢卡牌——那時、現在、永遠在一起。",
      subtitleLine2: "寶可夢卡牌遊戲 30 週年慶祝——和皮卡丘一起開派對吧！",
      sectionTitle: "奇蹟的三十年——慶祝正式開始",
      sectionBody:
        "寶可夢卡牌遊戲 30 週年慶祝登場！皮卡丘、夢幻、超夢等經典夥伴再次齊聚。重新連結友情的羈絆，享受前所未有的慶祝吧！",
      specs: [
        { label: "發售日", value: "2026年9月16日（三）" },
        { label: "系列", value: "寶可夢卡牌遊戲 MEGA" },
        { label: "語言", value: "日文" },
        {
          label: "內容",
          value: "收錄 30 週年紀念卡、皮卡丘，以及幻之寶可夢冒險卡",
        },
      ],
      asideTitle: "📍 準備好加入慶祝了嗎？",
      asideBody: "想入手最新補充包？找尋附近的官方認證卡牌店舖，立刻預購吧！",
      ctaText: "尋找認證店舖",
      filterHint: "我們會篩選販售寶可夢商品的店舖",
    },
  },

  "onepiece-op17": {
    ja: {
      badgeText: "⏳ 2026年8月22日 発売",
      h1Line1: "[OP-17] ブースターパック",
      h1Accent: "「世界最強の戦士」",
      subtitleLine1: "世界を揺るがす大戦が、再び……！",
      subtitleLine2:
        "エルバフへの伝説の航海をテーマにしたブースターパック第17弾。",
      sectionTitle: "世界最強に挑め——エルバフへ踏み出せ",
      sectionBody:
        "ONE PIECEカードゲーム ブースターパック第17弾が、ゲーム4周年を記念して登場！舞台はエルバフ。世界最強の戦士たちが集う。きずなを再び結び、新たな冒険へ出航しよう！",
      specs: [
        { label: "発売日", value: "2026年8月22日（土）" },
        { label: "シリーズ", value: "ONE PIECEカードゲーム" },
        { label: "言語", value: "日本語" },
        {
          label: "内容",
          value: "エルバフ、巨人の戦士、冒険をテーマにしたカードを収録",
        },
      ],
      asideTitle: "📍 エルバフへ出航する準備はできた？",
      asideBody:
        "最新のブースターパックが欲しい？近くの公式認定カードショップを見つけて、今すぐ予約しよう！",
      ctaText: "認定店舗を探す",
      filterHint: "ONE PIECE商品を取り扱う店舗で絞り込みます",
    },
    "zh-TW": {
      badgeText: "⏳ 2026年8月22日 發售",
      h1Line1: "[OP-17] 補充包",
      h1Accent: "「世界最強の戦士」",
      subtitleLine1: "撼動世界的大戰，再次降臨……！",
      subtitleLine2: "以通往艾爾巴夫的傳奇航程為主題的第 17 彈補充包。",
      sectionTitle: "挑戰世界最強——踏上艾爾巴夫",
      sectionBody:
        "ONE PIECE 卡牌遊戲補充包第 17 彈登場，歡慶遊戲四週年！舞台轉往艾爾巴夫，世界最強的戰士齊聚一堂。重新連結羈絆，啟航全新冒險吧！",
      specs: [
        { label: "發售日", value: "2026年8月22日（六）" },
        { label: "系列", value: "ONE PIECE 卡牌遊戲" },
        { label: "語言", value: "日文" },
        {
          label: "內容",
          value: "收錄以艾爾巴夫、巨人戰士與冒險為主題的卡牌",
        },
      ],
      asideTitle: "📍 準備好航向艾爾巴夫了嗎？",
      asideBody: "想入手最新補充包？找尋附近的官方認證卡牌店舖，立刻預購吧！",
      ctaText: "尋找認證店舖",
      filterHint: "我們會篩選販售 ONE PIECE 商品的店舖",
    },
  },

  "gundam-freedom-ascension": {
    ja: {
      badgeText: "⏳ 2026年7月24日 発売",
      h1Line1: "[GD-05] ブースターパック",
      h1Accent: "Freedom Ascension",
      subtitleLine1: "GUNDAM CARD GAME 1周年を記念して！",
      subtitleLine2:
        "人気のストライクフリーダムガンダムとνガンダムが、待望の登場。",
      sectionTitle: "自由の翼——星々を切り裂いて",
      sectionBody:
        "GUNDAM CARD GAMEが記念すべき1周年を迎える！ブースターパック第5弾 Freedom Ascensionは、最も象徴的な伝説のモビルスーツを届ける。ストライクフリーダムガンダムとνガンダムの追加で、戦場は一変する。パイロットたちよ、自由の翼を広げ、新たな勝利を手に入れろ！",
      specs: [
        { label: "発売日", value: "2026年7月24日" },
        { label: "シリーズ", value: "GUNDAM CARD GAME" },
        { label: "言語", value: "英語" },
        {
          label: "内容",
          value:
            "1周年特別収録：ストライクフリーダムガンダム、νガンダムほか人気モビルスーツ",
        },
      ],
      asideTitle: "📍 出撃の準備はできた？",
      asideBody:
        "最新のブースターパックが欲しい？近くの公式認定カードショップを見つけて、今すぐ予約しよう！",
      ctaText: "認定店舗を探す",
      filterHint: "ガンダム商品を取り扱う店舗で絞り込みます",
    },
    "zh-TW": {
      badgeText: "⏳ 2026年7月24日 發售",
      h1Line1: "[GD-05] 補充包",
      h1Accent: "Freedom Ascension",
      subtitleLine1: "慶祝 GUNDAM CARD GAME 一週年！",
      subtitleLine2: "人氣機體攻擊自由鋼彈與 ν 鋼彈，終於登場。",
      sectionTitle: "自由之翼——劃破星空",
      sectionBody:
        "GUNDAM CARD GAME 迎來難忘的一週年！補充包第 5 彈 Freedom Ascension 帶來最經典的傳奇機動戰士。攻擊自由鋼彈與 ν 鋼彈的加入，將徹底改寫戰場。駕駛員們，展開自由之翼，奪取全新勝利吧！",
      specs: [
        { label: "發售日", value: "2026年7月24日" },
        { label: "系列", value: "GUNDAM CARD GAME" },
        { label: "語言", value: "英文" },
        {
          label: "內容",
          value: "一週年特別收錄：攻擊自由鋼彈、ν 鋼彈及其他人氣機動戰士",
        },
      ],
      asideTitle: "📍 準備好出擊了嗎？",
      asideBody: "想入手最新補充包？找尋附近的官方認證卡牌店舖，立刻預購吧！",
      ctaText: "尋找認證店舖",
      filterHint: "我們會篩選販售鋼彈商品的店舖",
    },
  },

  "mtg-hobbit": {
    ja: {
      badgeText: "⏳ 発売中",
      h1Line1: "マジック：ザ・ギャザリング クロスオーバー",
      h1Accent: "「THE HOBBIT」",
      subtitleLine1: "GO THERE AND BACK AGAIN",
      subtitleLine2: "ビルボと一緒に、行きて帰りし壮大な冒険へ！",
      sectionTitle: "思いがけない旅",
      sectionBody:
        "ビルボと忘れられない冒険に出かけよう！ドワーフと友となり、トロルを出し抜き、エルフと知恵を競い、冒険の歌を歌え。旅の果てには黄金——あるいは竜の炎が待つかもしれない。旅そのものを楽しもう！",
      specs: [
        { label: "発売日", value: "発売中" },
        {
          label: "シリーズ",
          value: "マジック：ザ・ギャザリング Universes Beyond",
        },
        { label: "言語", value: "英語" },
        {
          label: "内容",
          value:
            "『ホビット』限定カード、クラシックキャラクター、新メカニクスを収録",
        },
      ],
      asideTitle: "📍 旅立ちの準備はできた？",
      asideBody:
        "最新のブースターパックが欲しい？近くの公式認定カードショップを見つけて、今すぐ手に入れよう！",
      ctaText: "認定店舗を探す",
      filterHint: "マジック：ザ・ギャザリング商品を取り扱う店舗で絞り込みます",
    },
    "zh-TW": {
      badgeText: "⏳ 現正販售",
      h1Line1: "魔法風雲會跨界合作",
      h1Accent: "「THE HOBBIT」",
      subtitleLine1: "GO THERE AND BACK AGAIN",
      subtitleLine2: "跟著比爾博踏上史詩冒險——去了又回來！",
      sectionTitle: "一場意外的旅程",
      sectionBody:
        "跟著比爾博展開難忘冒險！與矮人結為夥伴、智取巨魔、與精靈較量機智，並高唱冒險之歌。旅程盡頭或許有黃金——也可能是巨龍之火。享受這趟旅途吧！",
      specs: [
        { label: "發售日", value: "現正販售" },
        {
          label: "系列",
          value: "魔法風雲會 Universes Beyond",
        },
        { label: "語言", value: "英文" },
        {
          label: "內容",
          value: "收錄《哈比人》限定卡、經典角色與全新機制",
        },
      ],
      asideTitle: "📍 準備好踏上旅程了嗎？",
      asideBody: "想入手最新補充包？找尋附近的官方認證卡牌店舖，立刻入手吧！",
      ctaText: "尋找認證店舖",
      filterHint: "我們會篩選販售魔法風雲會商品的店舖",
    },
  },

  "dragonball-story-booster-01": {
    ja: {
      badgeText: "⏳ 2026年8月8日 発売",
      h1Line1: "[ST-01] ストーリーブースター",
      h1Accent: "STORY BOOSTER 01",
      subtitleLine1: "名場面が帰ってくる——超サイヤ人3孫悟空対魔人ブウ！",
      subtitleLine2:
        "ドラゴンボール超フュージョンワールド初のストーリーブースターが登場。",
      sectionTitle: "ブウとの激突——懐かしさが突き抜ける",
      sectionBody:
        "ドラゴンボール超カードゲーム フュージョンワールドの新シリーズ、STORY BOOSTER 01が登場！魔人ブウ編の名場面を再び——超サイヤ人3孫悟空と純粋魔人ブウが卓上で激突する。全85種のカードで、忘れられないあの瞬間へ戻ろう！",
      specs: [
        { label: "発売日", value: "2026年8月8日" },
        {
          label: "シリーズ",
          value: "ドラゴンボール超カードゲーム フュージョンワールド",
        },
        { label: "言語", value: "日本語" },
        {
          label: "内容",
          value: "魔人ブウ編のクラシックキャラクターと名場面を収録（全85種）",
        },
      ],
      asideTitle: "📍 名場面を追体験する準備はできた？",
      asideBody:
        "最新のブースターパックが欲しい？近くの公式認定カードショップを見つけて、今すぐ予約しよう！",
      ctaText: "認定店舗を探す",
      filterHint: "ドラゴンボール商品を取り扱う店舗で絞り込みます",
    },
    "zh-TW": {
      badgeText: "⏳ 2026年8月8日 發售",
      h1Line1: "[ST-01] Story Booster",
      h1Accent: "STORY BOOSTER 01",
      subtitleLine1: "經典對決回歸——超級賽亞人3孫悟空對上魔人普烏！",
      subtitleLine2: "七龍珠超 Fusion World 首款 Story Booster 登場。",
      sectionTitle: "與普烏的激戰——懷舊直擊",
      sectionBody:
        "七龍珠超卡牌遊戲 Fusion World 全新系列 STORY BOOSTER 01 登場！重溫魔人普烏篇經典——超級賽亞人3孫悟空與純粹魔人普烏再次在桌面交鋒。全 85 張卡牌，重返那些難忘瞬間！",
      specs: [
        { label: "發售日", value: "2026年8月8日" },
        {
          label: "系列",
          value: "七龍珠超卡牌遊戲 Fusion World",
        },
        { label: "語言", value: "日文" },
        {
          label: "內容",
          value: "收錄魔人普烏篇經典角色與名場面（共 85 張）",
        },
      ],
      asideTitle: "📍 準備好重溫經典了嗎？",
      asideBody: "想入手最新補充包？找尋附近的官方認證卡牌店舖，立刻預購吧！",
      ctaText: "尋找認證店舖",
      filterHint: "我們會篩選販售七龍珠商品的店舖",
    },
  },

  "pokemon-storm-emerald": {
    ja: {
      badgeText: "⏳ 2026年7月31日 発売",
      h1Line1: "MEGA ブースターセット",
      h1Accent: "ストームエメラルド",
      subtitleLine1: "伝説の支配者——メガレックウザexが降臨！",
      subtitleLine2: "エメラルドの嵐に乗り、果てなき空を支配せよ。",
      sectionTitle: "エメラルドの嵐——空を一掃する",
      sectionBody:
        "最新のポケモンカードゲームシリーズ、MEGA：ストームエメラルドが、空を支配する強大な力を届ける！伝説の支配者メガレックウザexが降臨し、荒れ狂うエメラルドの嵐ですべての障害を切り裂く。天空からの伝説の力を掌握し、戦いで風を統べろ！",
      specs: [
        { label: "発売日", value: "2026年7月31日（金）" },
        { label: "シリーズ", value: "ポケモンカードゲーム MEGA" },
        { label: "言語", value: "日本語" },
        {
          label: "内容",
          value:
            "メガレックウザex、および嵐をテーマにしたポケモン・トレーナーズカードを収録",
        },
      ],
      asideTitle: "📍 嵐に乗る準備はできた？",
      asideBody:
        "最新のブースターパックが欲しい？近くの公式認定カードショップを見つけて、今すぐ予約しよう！",
      ctaText: "認定店舗を探す",
      filterHint: "ポケモン商品を取り扱う店舗で絞り込みます",
    },
    "zh-TW": {
      badgeText: "⏳ 2026年7月31日 發售",
      h1Line1: "MEGA 補充包套組",
      h1Accent: "Storm Emerald（ストームエメラルド）",
      subtitleLine1: "傳說的支配者——超級烈空坐 ex 降臨！",
      subtitleLine2: "乘上翠綠風暴，主宰無盡蒼穹。",
      sectionTitle: "翠綠風暴——席捲天空",
      sectionBody:
        "最新寶可夢卡牌遊戲系列 MEGA：Storm Emerald，帶來主宰天空的強大力量！傳說的支配者超級烈空坐 ex 降臨，以狂暴的翠綠風暴撕裂一切障礙。掌握這股來自天際的傳說之力，在對戰中統御風勢吧！",
      specs: [
        { label: "發售日", value: "2026年7月31日（五）" },
        { label: "系列", value: "寶可夢卡牌遊戲 MEGA" },
        { label: "語言", value: "日文" },
        {
          label: "內容",
          value: "收錄超級烈空坐 ex，以及風暴主題寶可夢與訓練家卡",
        },
      ],
      asideTitle: "📍 準備好乘上風暴了嗎？",
      asideBody: "想入手最新補充包？找尋附近的官方認證卡牌店舖，立刻預購吧！",
      ctaText: "尋找認證店舖",
      filterHint: "我們會篩選販售寶可夢商品的店舖",
    },
  },

  "pokemon-abyss-eye": {
    ja: {
      badgeText: "⏳ 2026年5月22日 発売",
      h1Line1: "MEGA ブースターセット",
      h1Accent: "アビスアイ",
      subtitleLine1: "闇のまなざし——メガダークライexが襲来！",
      subtitleLine2: "果てなき深淵に飲み込まれるか、それとも闇の力を制するか？",
      sectionTitle: "深淵が降り立つ——悪夢の支配",
      sectionBody:
        "最新のポケモンカードゲームシリーズ、MEGA：アビスアイが、恐るべき闇の力を届ける！アビスアイを宿すメガダークライexが、対戦相手を目覚められない悪夢へと引きずり込む。未知の力を手にし、メタゲームを支配せよ！",
      specs: [
        { label: "発売日", value: "2026年5月22日（金）" },
        { label: "シリーズ", value: "ポケモンカードゲーム MEGA" },
        { label: "言語", value: "日本語" },
        {
          label: "内容",
          value:
            "メガダークライex、および深淵をテーマにしたポケモン・トレーナーズカードを収録",
        },
      ],
      asideTitle: "📍 深淵を見つめる準備はできた？",
      asideBody:
        "最新のブースターパックが欲しい？近くの公式認定カードショップを見つけて、今すぐ予約しよう！",
      ctaText: "認定店舗を探す",
      filterHint: "ポケモン商品を取り扱う店舗で絞り込みます",
    },
    "zh-TW": {
      badgeText: "⏳ 2026年5月22日 發售",
      h1Line1: "MEGA 補充包套組",
      h1Accent: "Abyss Eye（アビスアイ）",
      subtitleLine1: "黑暗的凝視——超級達克萊伊 ex 襲來！",
      subtitleLine2: "你會被無盡深淵吞噬，還是掌握黑暗之力？",
      sectionTitle: "深淵降臨——惡夢的支配",
      sectionBody:
        "最新寶可夢卡牌遊戲系列 MEGA：Abyss Eye，帶來可怕的黑暗力量！身懷深淵之眼的超級達克萊伊 ex，將對手拖入無法醒來的惡夢。奪取這股未知之力，主宰環境吧！",
      specs: [
        { label: "發售日", value: "2026年5月22日（五）" },
        { label: "系列", value: "寶可夢卡牌遊戲 MEGA" },
        { label: "語言", value: "日文" },
        {
          label: "內容",
          value: "收錄超級達克萊伊 ex，以及深淵主題寶可夢與訓練家卡",
        },
      ],
      asideTitle: "📍 準備好凝視深淵了嗎？",
      asideBody: "想入手最新補充包？找尋附近的官方認證卡牌店舖，立刻預購吧！",
      ctaText: "尋找認證店舖",
      filterHint: "我們會篩選販售寶可夢商品的店舖",
    },
  },

  "onepiece-op16": {
    ja: {
      badgeText: "⏳ 2026年5月30日 発売",
      h1Line1: "[OP-16] ブースターパック",
      h1Accent: "「決戦の時」",
      subtitleLine1: "世界を揺るがす大戦が、再び……！",
      subtitleLine2: "伝説の頂上戦争をテーマにしたブースターパック第16弾。",
      sectionTitle: "頂上戦争——クラシックが再び",
      sectionBody:
        "ONE PIECEカードゲーム ブースターパック第16弾が、伝説の頂上戦争編へ回帰！海軍本部の絶対的正義と白ひげ海賊団が、再びあなたの卓上で激突する。仲間を集め、兄弟のきずなを武器に、世界の運命を変えろ！",
      specs: [
        { label: "発売日", value: "2026年5月30日（土）" },
        { label: "シリーズ", value: "ONE PIECEカードゲーム" },
        { label: "言語", value: "日本語" },
        {
          label: "内容",
          value: "海軍本部と白ひげ海賊団のクロスオーバーカードを収録",
        },
      ],
      asideTitle: "📍 頂上戦争に参加する準備はできた？",
      asideBody:
        "最新のブースターパックが欲しい？近くの公式認定カードショップを見つけて、今すぐ予約しよう！",
      ctaText: "認定店舗を探す",
      filterHint: "ONE PIECE商品を取り扱う店舗で絞り込みます",
    },
    "zh-TW": {
      badgeText: "⏳ 2026年5月30日 發售",
      h1Line1: "[OP-16] 補充包",
      h1Accent: "決戰時刻",
      subtitleLine1: "撼動世界的大戰，再次降臨……！",
      subtitleLine2: "以傳奇頂上戰爭為主題的第 16 彈補充包。",
      sectionTitle: "頂上戰爭——經典重現",
      sectionBody:
        "ONE PIECE 卡牌遊戲補充包第 16 彈回歸傳說中的頂上戰爭篇！海軍本部的絕對正義與白鬍子海賊團，再次在你的桌面交鋒。召集夥伴、運用兄弟羈絆，改寫世界的命運吧！",
      specs: [
        { label: "發售日", value: "2026年5月30日（六）" },
        { label: "系列", value: "ONE PIECE 卡牌遊戲" },
        { label: "語言", value: "日文" },
        {
          label: "內容",
          value: "收錄海軍本部與白鬍子海賊團跨界卡牌",
        },
      ],
      asideTitle: "📍 準備好加入頂上戰爭了嗎？",
      asideBody: "想入手最新補充包？找尋附近的官方認證卡牌店舖，立刻預購吧！",
      ctaText: "尋找認證店舖",
      filterHint: "我們會篩選販售 ONE PIECE 商品的店舖",
    },
  },

  "gundam-phantom-aria": {
    ja: {
      badgeText: "⏳ 2026年4月25日 発売",
      h1Line1: "[GD-04] ブースターパック",
      h1Accent: "ファントム アリア",
      subtitleLine1: "宇宙をわたる、幻のソナタ……",
      subtitleLine2:
        "GUNDAM CARD GAME ブースターパック第4弾——ユニコーンが目覚める！",
      sectionTitle: "NT-D起動——ファントムが目覚める",
      sectionBody:
        "GUNDAM CARD GAME ブースターパック第4弾 ファントム アリアが、戦場を深宇宙へ！ユニコーンガンダムを筆頭に、強力なモビルスーツとサイコフレーム技術が革命的な戦術シフトをもたらす。パイロットたちを率い、星々のあいだで勝利のアリアを響かせろ！",
      specs: [
        { label: "発売日", value: "2026年4月25日" },
        { label: "シリーズ", value: "GUNDAM CARD GAME" },
        { label: "言語", value: "日本語" },
        {
          label: "内容",
          value:
            "ユニコーンガンダム系列のモビルスーツと専用パイロットカードを収録",
        },
      ],
      asideTitle: "📍 出撃の準備はできた？",
      asideBody:
        "最新のブースターパックが欲しい？近くの公式認定カードショップを見つけて、今すぐ予約しよう！",
      ctaText: "認定店舗を探す",
      filterHint: "ガンダム商品を取り扱う店舗で絞り込みます",
    },
    "zh-TW": {
      badgeText: "⏳ 2026年4月25日 發售",
      h1Line1: "[GD-04] 補充包",
      h1Accent: "Phantom Aria（ファントム アリア）",
      subtitleLine1: "穿越宇宙的幻影奏鳴曲……",
      subtitleLine2: "GUNDAM CARD GAME 補充包第 4 彈——獨角獸覺醒！",
      sectionTitle: "NT-D 啟動——幻影覺醒",
      sectionBody:
        "GUNDAM CARD GAME 補充包第 4 彈 Phantom Aria，將戰場帶向深空！以獨角獸鋼彈為首，強大機動戰士與精神感應框架技術帶來革命性戰術轉變。率領你的駕駛員，在星辰之間奏響勝利的詠歎吧！",
      specs: [
        { label: "發售日", value: "2026年4月25日" },
        { label: "系列", value: "GUNDAM CARD GAME" },
        { label: "語言", value: "日文" },
        {
          label: "內容",
          value: "收錄獨角獸鋼彈系列機動戰士與專屬駕駛員卡",
        },
      ],
      asideTitle: "📍 準備好出擊了嗎？",
      asideBody: "想入手最新補充包？找尋附近的官方認證卡牌店舖，立刻預購吧！",
      ctaText: "尋找認證店舖",
      filterHint: "我們會篩選販售鋼彈商品的店舖",
    },
  },

  "mtg-marvel-super-heroes": {
    ja: {
      badgeText: "⏳ 2026年6月26日 発売",
      h1Line1: "マジック：ザ・ギャザリング クロスオーバー",
      h1Accent: "Marvel Super Heroes",
      subtitleLine1: "PLAYERS ASSEMBLE!",
      subtitleLine2:
        "ふたつの伝説の世界が衝突——マジック史上最大級のクロスオーバー！",
      sectionTitle: "伝説の力を、その手に",
      sectionBody:
        "マジック：ザ・ギャザリングがマーベル・スーパーヒーローズと前例のないクロスオーバーを実現。地球のヒーローとヴィランが、収集・プレイ・デッキ構築できるカードに——伝説の力を、まさにその手に！",
      specs: [
        { label: "発売日", value: "2026年6月26日" },
        {
          label: "シリーズ",
          value: "マジック：ザ・ギャザリング Universes Beyond",
        },
        { label: "言語", value: "英語" },
        {
          label: "内容",
          value:
            "マーベル・スーパーヒーローズ＆ヴィラン限定カード、および新メカニクスを収録",
        },
      ],
      asideTitle: "📍 ヒーローを集結させる準備はできた？",
      asideBody:
        "最新のブースターパックが欲しい？近くの公式認定カードショップを見つけて、今すぐ予約しよう！",
      ctaText: "認定店舗を探す",
      filterHint: "マジック：ザ・ギャザリング商品を取り扱う店舗で絞り込みます",
    },
    "zh-TW": {
      badgeText: "⏳ 2026年6月26日 發售",
      h1Line1: "魔法風雲會跨界合作",
      h1Accent: "Marvel Super Heroes",
      subtitleLine1: "PLAYERS ASSEMBLE!",
      subtitleLine2: "兩個傳奇世界碰撞——魔法風雲會史上最大跨界！",
      sectionTitle: "傳奇之力，就在你手中",
      sectionBody:
        "魔法風雲會與漫威超級英雄展開前所未有的跨界合作。地球上你最愛的英雄與反派，如今化為可收藏、對戰、組牌的卡牌——把傳奇之力握在手中吧！",
      specs: [
        { label: "發售日", value: "2026年6月26日" },
        {
          label: "系列",
          value: "魔法風雲會 Universes Beyond",
        },
        { label: "語言", value: "英文" },
        {
          label: "內容",
          value: "收錄漫威超級英雄與反派限定卡，以及全新機制",
        },
      ],
      asideTitle: "📍 準備好召集你的英雄了嗎？",
      asideBody: "想入手最新補充包？找尋附近的官方認證卡牌店舖，立刻預購吧！",
      ctaText: "尋找認證店舖",
      filterHint: "我們會篩選販售魔法風雲會商品的店舖",
    },
  },

  "mtg-strixhaven": {
    ja: {
      badgeText: "⏳ 2026年4月24日 発売",
      h1Line1: "マジック：ザ・ギャザリング ブースターセット",
      h1Accent: "ストリクスヘイヴンの秘密",
      subtitleLine1: "学校が、本気になる。",
      subtitleLine2: "教科書を置き、五つの学部の裏に隠された真実を暴け！",
      sectionTitle: "魔道学校の試練——真実が明かされる",
      sectionBody:
        "ストリクスヘイヴンへようこそ！『ストリクスヘイヴンの秘密』では、魔法大学の日常がひっくり返り、五つの学部の生徒たちは教科書を離れ、現実の世界へ踏み出し、学校の歴史に眠る古代の秘密を暴かなければならない。あらゆる呪文を唱え、自分の学部が最強であることを証明せよ！",
      specs: [
        { label: "発売日", value: "2026年4月24日" },
        { label: "シリーズ", value: "マジック：ザ・ギャザリング" },
        { label: "言語", value: "英語" },
        {
          label: "内容",
          value:
            "五つの学部のシグネチャースペル、プレインズウォーカー、新伝説クリーチャーを収録",
        },
      ],
      asideTitle: "📍 入学チャレンジの準備はできた？",
      asideBody:
        "最新のブースターパックが欲しい？近くの公式認定カードショップを見つけて、今すぐ予約しよう！",
      ctaText: "認定店舗を探す",
      filterHint: "マジック：ザ・ギャザリング商品を取り扱う店舗で絞り込みます",
    },
    "zh-TW": {
      badgeText: "⏳ 2026年4月24日 發售",
      h1Line1: "魔法風雲會補充包套組",
      h1Accent: "斯翠海文的秘密",
      subtitleLine1: "學校這次來真的了。",
      subtitleLine2: "放下教科書，揭開五大學院背後隱藏的真相！",
      sectionTitle: "魔道學院的試煉——真相揭曉",
      sectionBody:
        "歡迎回到斯翠海文！在《斯翠海文的秘密》中，魔法大學的日常徹底翻轉。五大學院的學生必須放下書本、踏入現實世界，揭開沉睡於校史中的遠古秘密。施展你能用的每一道咒語，證明你的學院才是最強！",
      specs: [
        { label: "發售日", value: "2026年4月24日" },
        { label: "系列", value: "魔法風雲會" },
        { label: "語言", value: "英文" },
        {
          label: "內容",
          value: "收錄五大學院代表咒語、鵬洛客，以及全新傳奇生物",
        },
      ],
      asideTitle: "📍 準備好迎接入學挑戰了嗎？",
      asideBody: "想入手最新補充包？找尋附近的官方認證卡牌店舖，立刻預購吧！",
      ctaText: "尋找認證店舖",
      filterHint: "我們會篩選販售魔法風雲會商品的店舖",
    },
  },

  "dragonball-fb10": {
    ja: {
      badgeText: "⏳ 2026年6月12日 発売",
      h1Line1: "[FB-10] ブースターパック",
      h1Accent: "CROSS FORCE",
      subtitleLine1:
        "悟空とベジータの究極の激突——融合の力があらゆる境界を越える！",
      subtitleLine2:
        "ドラゴンボール超フュージョンワールド ブースターパック第10弾が登場。",
      sectionTitle: "限界の先へ——戦いが帰ってくる",
      sectionBody:
        "ドラゴンボール超カードゲーム フュージョンワールド最新弾 CROSS FORCEが、かつてないバトル体験を届ける！超サイヤ人ブルーの力が卓上で炸裂——究極の戦士ラインナップを組み、融合と境界を越える力を解き放ち、対戦相手を叩き潰せ！",
      specs: [
        { label: "発売日", value: "2026年6月12日" },
        {
          label: "シリーズ",
          value: "ドラゴンボール超カードゲーム フュージョンワールド",
        },
        { label: "言語", value: "英語" },
        {
          label: "内容",
          value:
            "超サイヤ人ブルーほか強力キャラクターカード、および新戦術カードを収録",
        },
      ],
      asideTitle: "📍 力を解き放つ準備はできた？",
      asideBody:
        "最新のブースターパックが欲しい？近くの公式認定カードショップを見つけて、今すぐ予約しよう！",
      ctaText: "認定店舗を探す",
      filterHint: "ドラゴンボール商品を取り扱う店舗で絞り込みます",
    },
    "zh-TW": {
      badgeText: "⏳ 2026年6月12日 發售",
      h1Line1: "[FB-10] 補充包",
      h1Accent: "CROSS FORCE",
      subtitleLine1: "悟空與貝吉塔的終極對決——融合之力跨越一切界限！",
      subtitleLine2: "七龍珠超 Fusion World 補充包第 10 彈登場。",
      sectionTitle: "超越極限——戰鬥回歸",
      sectionBody:
        "七龍珠超卡牌遊戲 Fusion World 最新擴充 CROSS FORCE，帶來前所未有的對戰體驗！超級賽亞人藍之力在桌面爆發——組建你的終極戰士陣容，釋放融合與越界之力，擊潰對手吧！",
      specs: [
        { label: "發售日", value: "2026年6月12日" },
        {
          label: "系列",
          value: "七龍珠超卡牌遊戲 Fusion World",
        },
        { label: "語言", value: "英文" },
        {
          label: "內容",
          value: "收錄超級賽亞人藍等強力角色卡，以及全新戰術卡",
        },
      ],
      asideTitle: "📍 準備好釋放你的力量了嗎？",
      asideBody: "想入手最新補充包？找尋附近的官方認證卡牌店舖，立刻預購吧！",
      ctaText: "尋找認證店舖",
      filterHint: "我們會篩選販售七龍珠商品的店舖",
    },
  },

  "mtg-reality-fracture": {
    ja: {
      badgeText: "⏳ 2026年10月2日 発売",
      h1Line1: "マジック：ザ・ギャザリング — 新セット",
      h1Accent: "「Reality Fracture」",
      subtitleLine1: "新メカニクスと Booster Fun の詳細が公開！",
      subtitleLine2: "伝説の戦略カードゲーム——現実と幻想が裂け目で交わる。",
      sectionTitle: "現実が裂ける——新メカニクス登場",
      sectionBody:
        "ウィザーズ・オブ・ザ・コーストが贈るマジック：ザ・ギャザリング最新セット、Reality Fracture。世界中で5,000万人以上がプレイするオリジナル戦略カードゲームに、強力な新メカニクスとコレクター向け Booster Fun 仕様が登場。現実の裂け目で、無限の魔法の力を見つけよう！",
      specs: [
        {
          label: "発売日",
          value: "2026年10月2日（金）（店舗先行：9月25日）",
        },
        { label: "シリーズ", value: "マジック：ザ・ギャザリング" },
        { label: "言語", value: "日本語" },
        {
          label: "内容",
          value:
            "強力な新メカニクスカード、Booster Fun 特別枠、およびフォイルを収録",
        },
      ],
      asideTitle: "📍 裂け目を越える準備はできた？",
      asideBody:
        "最新のブースターパックが欲しい？近くの公式認定カードショップを見つけて、今すぐ予約しよう！",
      ctaText: "認定店舗を探す",
      filterHint: "マジック：ザ・ギャザリング商品を取り扱う店舗で絞り込みます",
    },
    "zh-TW": {
      badgeText: "⏳ 2026年10月2日 發售",
      h1Line1: "魔法風雲會 — 新系列",
      h1Accent: "「Reality Fracture」",
      subtitleLine1: "全新機制與 Booster Fun 細節公開！",
      subtitleLine2: "傳奇策略卡牌遊戲——現實與幻象在裂縫中交會。",
      sectionTitle: "現實崩裂——全新機制降臨",
      sectionBody:
        "威世智推出魔法風雲會最新系列 Reality Fracture。這款全球超過 5,000 萬玩家的原創策略卡牌遊戲，帶來強力全新機制與收藏向 Booster Fun 特仕處理。在現實的裂縫中，發掘無限魔法之力吧！",
      specs: [
        {
          label: "發售日",
          value: "2026年10月2日（五）（店家先行：9月25日）",
        },
        { label: "系列", value: "魔法風雲會" },
        { label: "語言", value: "日文" },
        {
          label: "內容",
          value: "收錄強力全新機制卡、Booster Fun 特仕框與閃卡",
        },
      ],
      asideTitle: "📍 準備好跨越裂縫了嗎？",
      asideBody: "想入手最新補充包？找尋附近的官方認證卡牌店舖，立刻預購吧！",
      ctaText: "尋找認證店舖",
      filterHint: "我們會篩選販售魔法風雲會商品的店舖",
    },
  },

  "dragonball-fb11": {
    ja: {
      badgeText: "⏳ 2026年9月12日 発売",
      h1Line1: "[FB-11] ブースターパック",
      h1Accent: "「BRIGHTNESS OF HOPE」",
      subtitleLine1: "希望の光——超サイヤ人3が舞台に降臨！",
      subtitleLine2:
        "ドラゴンボール超フュージョンワールド ブースターパック第11弾が登場。",
      sectionTitle: "限界を破れ——希望の光を灯せ",
      sectionBody:
        "ドラゴンボール超カードゲーム フュージョンワールド最新弾 BRIGHTNESS OF HOPEが登場！超サイヤ人3孫悟空を筆頭に、全123種のカードがさらに熱いバトルを届ける。希望の光を集め、絶望を打ち破り、卓上で真の力を示せ！",
      specs: [
        { label: "発売日", value: "2026年9月12日" },
        {
          label: "シリーズ",
          value: "ドラゴンボール超カードゲーム フュージョンワールド",
        },
        { label: "言語", value: "日本語" },
        {
          label: "内容",
          value: "全123種。超サイヤ人3および希望をテーマにした強力カードを収録",
        },
      ],
      asideTitle: "📍 気を解き放つ準備はできた？",
      asideBody:
        "最新のブースターパックが欲しい？近くの公式認定カードショップを見つけて、今すぐ予約しよう！",
      ctaText: "認定店舗を探す",
      filterHint: "ドラゴンボール商品を取り扱う店舗で絞り込みます",
    },
    "zh-TW": {
      badgeText: "⏳ 2026年9月12日 發售",
      h1Line1: "[FB-11] 補充包",
      h1Accent: "「BRIGHTNESS OF HOPE」",
      subtitleLine1: "希望之光——超級賽亞人3登上舞台！",
      subtitleLine2: "七龍珠超 Fusion World 補充包第 11 彈強勢登場。",
      sectionTitle: "突破極限——點燃希望之光",
      sectionBody:
        "七龍珠超卡牌遊戲 Fusion World 最新擴充 BRIGHTNESS OF HOPE 登場！以超級賽亞人3孫悟空為首，全 123 種卡牌帶來更熱血的對戰。聚集希望之光、粉碎絕望，在桌面上展現你的真正實力吧！",
      specs: [
        { label: "發售日", value: "2026年9月12日" },
        {
          label: "系列",
          value: "七龍珠超卡牌遊戲 Fusion World",
        },
        { label: "語言", value: "日文" },
        {
          label: "內容",
          value: "共 123 種，收錄超級賽亞人3與希望主題強力卡",
        },
      ],
      asideTitle: "📍 準備好點燃你的氣了嗎？",
      asideBody: "想入手最新補充包？找尋附近的官方認證卡牌店舖，立刻預購吧！",
      ctaText: "尋找認證店舖",
      filterHint: "我們會篩選販售七龍珠商品的店舖",
    },
  },

  "gundam-gd06-stardust-trails": {
    ja: {
      badgeText: "⏳ 予約受付中 | 2026年10月31日 発売",
      h1Line1: "[GD-06] ブースターパック",
      h1Accent: "「Stardust Trails」",
      subtitleLine1: "戦士たちの信念が、星屑の戦場に新たな軌跡を刻む。",
      subtitleLine2: "ガンダムカードゲーム第6弾——スターダスト作戦、開幕！",
      sectionTitle: "星屑の魂、宇宙で激突",
      sectionBody:
        "『ガンダムカードゲーム』第6弾「Stardust Trails」が、硝煙と星屑に満ちた宇宙戦場へ再びあなたを導く！モビルスーツ同士の宿命の対決を体験し、新たな機体と戦術を駆使して、果てなき星海に勝利の軌跡を刻もう！",
      specs: [
        { label: "発売日", value: "2026年10月31日" },
        { label: "シリーズ", value: "GUNDAM CARD GAME" },
        { label: "言語", value: "日本語" },
        {
          label: "内容",
          value:
            "スターダスト作戦関連の機体・パイロット、および新戦術カードを収録",
        },
      ],
      asideTitle: "📍 スターダスト作戦に臨む準備はできた？",
      asideBody:
        "最新のブースターパックが欲しい？近くの公式認定カードショップを見つけて、今すぐ予約しよう！",
      ctaText: "認定店舗を探す",
      filterHint: "ガンダム商品を取り扱う店舗で絞り込みます",
    },
    "zh-TW": {
      badgeText: "⏳ 2026/10/31 發售",
      h1Line1: "[GD-06] 補充包",
      h1Accent: "「Stardust Trails」",
      subtitleLine1: "戰士們的信念，在星屑的戰場上刻下新的軌跡。",
      subtitleLine2: "機動戰士鋼彈卡牌遊戲第 6 彈，星塵作戰展開！",
      sectionTitle: "星塵之魂，激戰宇宙",
      sectionBody:
        "《鋼彈卡牌遊戲》第 6 彈「Stardust Trails」帶你重返充滿硝煙與星塵的宇宙戰場！體驗機動戰士之間扣人心弦的宿命對決，活用全新機體與戰術，在無盡的星海中刻下屬於你的勝利軌跡！",
      specs: [
        { label: "發售日期", value: "2026 年 10 月 31 日" },
        { label: "遊戲系列", value: "GUNDAM CARD GAME" },
        { label: "語言版本", value: "日文版" },
        {
          label: "收錄內容",
          value: "收錄星塵作戰相關機體及駕駛員，全新戰術卡",
        },
      ],
      asideTitle: "📍 準備好展開星塵作戰？",
      asideBody: "想入手最新卡包？立即尋找你附近的官方認證卡牌店，搶先預購！",
      ctaText: "尋找官方認證店",
      filterHint: "將為您過濾販售 鋼彈 系列之店家",
    },
  },

  "onepiece-eb05-heroines-vol2": {
    ja: {
      badgeText: "⏳ 予約受付中 | 2026年10月31日 発売",
      h1Line1: "[EB-05] エクストラブースター",
      h1Accent: "「ONE PIECE Heroines Edition Vol.2」",
      subtitleLine1: "ヒロインが主役のエクストラブースター第2弾！",
      subtitleLine2: "美しきキャラクターたちを集めて、デッキを強化しよう！",
      sectionTitle: "華麗なヒロインたち、再び登場",
      sectionBody:
        "ONE PIECEカードゲーム [EB-05] エクストラブースターがヒロインテーマで再登場！「ONE PIECE Heroines Edition Vol.2」はロビン、ナミをはじめとする美しく強大な女性キャラクターを収録。魅力的で致命的な新たな戦力をデッキに加えよう！",
      specs: [
        { label: "発売日", value: "2026年10月31日（土）" },
        { label: "シリーズ", value: "ONE PIECEカードゲーム" },
        { label: "言語", value: "日本語" },
        {
          label: "内容",
          value: "人気の女性キャラクターカードを収録し、デッキ構築を強化",
        },
      ],
      asideTitle: "📍 ヒロインたちを集める準備はできた？",
      asideBody:
        "最新のブースターパックが欲しい？近くの公式認定カードショップを見つけて、今すぐ予約しよう！",
      ctaText: "認定店舗を探す",
      filterHint: "ONE PIECE商品を取り扱う店舗で絞り込みます",
    },
    "zh-TW": {
      badgeText: "⏳ 2026/10/31 發售",
      h1Line1: "[EB-05] 額外補充包",
      h1Accent: "「ONE PIECE Heroines Edition Vol.2」",
      subtitleLine1: "以女主角為主役的額外補充包第2彈！",
      subtitleLine2: "集結美麗的角色們來強化你的牌組吧！",
      sectionTitle: "華麗的女主角們，再次登場",
      sectionBody:
        "航海王卡牌遊戲 [EB-05] 額外補充包強勢回歸女主角主題！「ONE PIECE Heroines Edition Vol.2」收錄了以羅賓、娜美等眾多美麗且強大的女性角色。為你的牌組增添迷人又致命的全新戰力！",
      specs: [
        { label: "發售日期", value: "2026 年 10 月 31 日 [土]" },
        { label: "遊戲系列", value: "ONE PIECE Card Game" },
        { label: "語言版本", value: "日文版" },
        {
          label: "收錄內容",
          value: "主打航海王人氣女性角色卡牌，強化套牌構築",
        },
      ],
      asideTitle: "📍 準備好收集女主角們？",
      asideBody: "想入手最新卡包？立即尋找你附近的官方認證卡牌店，搶先預購！",
      ctaText: "尋找官方認證店",
      filterHint: "將為您過濾販售 航海王 系列之店家",
    },
  },
};

export function localizeProductEntry(
  entry: ProductInformationEntry,
  locale: AppLocale,
): ProductInformationEntry {
  if (locale === "en") return entry;
  const overlay = PRODUCT_COPY_OVERRIDES[entry.id]?.[locale];
  if (!overlay) return entry;
  return { ...entry, ...overlay };
}
